import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const HazardTrendAnalysis = ({ timeframe, location }) => {
  const trendData = [
    { month: 'Jul', critical: 15, high: 45, medium: 120, low: 65 },
    { month: 'Aug', critical: 18, high: 52, medium: 135, low: 72 },
    { month: 'Sep', critical: 16, high: 48, medium: 128, low: 68 },
    { month: 'Oct', critical: 20, high: 55, medium: 142, low: 78 },
    { month: 'Nov', critical: 17, high: 50, medium: 138, low: 75 },
    { month: 'Dec', critical: 18, high: 63, medium: 156, low: 87 },
  ];

  const categoryData = [
    { name: 'Equipment', value: 126, color: '#3B82F6' },
    { name: 'Process', value: 98, color: '#10B981' },
    { name: 'Chemical', value: 67, color: '#F59E0B' },
    { name: 'Operational', value: 33, color: '#EF4444' },
  ];

  const locationData = [
    { location: 'Offshore Alpha', hazards: 145 },
    { location: 'Offshore Bravo', hazards: 98 },
    { location: 'Refinery 01', hazards: 56 },
    { location: 'Terminal West', hazards: 25 },
  ];

  const COLORS = ['#EF4444', '#F59E0B', '#FBBF24', '#10B981'];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Hazard Trend Analysis</h2>
        <p className="text-sm text-gray-600 mt-1">
          Historical trends and distribution patterns
        </p>
      </div>
      {/* Hazard Trends Over Time */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Hazard Identification Trends</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="month" 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
              />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
              />
              <Line 
                type="monotone" 
                dataKey="critical" 
                stroke="#EF4444" 
                strokeWidth={2}
                name="Critical"
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="high" 
                stroke="#F59E0B" 
                strokeWidth={2}
                name="High"
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="medium" 
                stroke="#FBBF24" 
                strokeWidth={2}
                name="Medium"
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line 
                type="monotone" 
                dataKey="low" 
                stroke="#10B981" 
                strokeWidth={2}
                name="Low"
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Category and Location Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Hazard Distribution by Category</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categoryData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100)?.toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {categoryData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry?.color} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Location Comparison */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Hazards by Location</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={locationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="location" 
                  stroke="#6B7280"
                  style={{ fontSize: '11px' }}
                  angle={-15}
                  textAnchor="end"
                  height={60}
                />
                <YAxis 
                  stroke="#6B7280"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}
                />
                <Bar 
                  dataKey="hazards" 
                  fill="#3B82F6"
                  radius={[8, 8, 0, 0]}
                  name="Total Hazards"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HazardTrendAnalysis;